/**
 * Browser-safe logger shim.
 *
 * When `@speech-agent/logger` is resolved in a browser context the
 * conditional export in package.json points here instead of the real
 * pino-based server logger.  Every method delegates to `console.*` so
 * log output still appears in DevTools while avoiding Node-only deps.
 */

type LogFn = (...args: unknown[]) => void;

type BrowserLogger = {
  trace: LogFn;
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
  fatal: LogFn;
  child: (
    bindings: Record<string, unknown>
  ) => BrowserLogger;
};

function createBrowserLogger(): BrowserLogger {
  const self: BrowserLogger = {
    trace: console.debug.bind(console),
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    fatal: console.error.bind(console),
    child: (_bindings) => self, // bindings ignored in browser shim
  };
  return self;
}

export async function createLoggerInstance(): Promise<BrowserLogger> {
  return createBrowserLogger();
}

export const logger: BrowserLogger = createBrowserLogger();

export function createLogger(
  _bindings: Record<string, unknown>,
  baseLogger: BrowserLogger = logger
): BrowserLogger {
  return baseLogger;
}

export type Logger = BrowserLogger;
